import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { t } from '@lingui/core/macro';
import { useQuery } from '@tanstack/react-query';
import { apiUrl } from '@lib/functions/Api';
import { ApiEndpoints } from '@lib/enums/ApiEndpoints';
import { ModelType } from '@lib/enums/ModelType';
import type { ApiFormFieldSet } from '@lib/types/Forms';
import { formatDisplayValue } from '@lib/functions/Conversion';
import { formatUnit } from '@lib/functions/Units';
import { useApi } from '../../contexts/ApiContext';
import { formatCurrency, formatDate, formatDecimal } from '../../defaults/formatters';

dayjs.extend(utc);
dayjs.extend(timezone);

export const inventoryUrl = '/api/techni-serre/inventory/';
export const countUrl = ApiEndpoints.inventory_count_list;
export const lineUrl = `${inventoryUrl}count-lines/`;
export const statementUrl = ApiEndpoints.inventory_statement_list;
export const inventoryBreadcrumb = () => ({ name: t`Inventory`, url: '/part/category/index/parts' });
export const countsBreadcrumb = () => ({ name: t`Counts`, url: '/part/category/index/counts' });
export const statementsBreadcrumb = () => ({ name: t`Inventory value`, url: '/part/category/index/statements' });
export const countStatus = (status: string, counted = true) => !counted ? t`Not counted` : status === 'applied' ? t`Applied` : status === 'cancelled' ? t`Cancelled` : t`Draft`;
export const quantity = (value: any, units?: string) => value == null ? formatDisplayValue(null) : `${formatDecimal(value, {digits: units === 'count' ? 0 : 5})} ${formatUnit(units)}`;
export const money = (value: any, unitCost = false) => value == null ? formatDisplayValue(null) : formatCurrency(value, {currency:'CAD',digits:unitCost?6:2,minDigits:unitCost?0:2});
export const dateTime = (value: string) => value ? formatDate(dayjs(value).tz('America/Toronto').format('YYYY-MM-DDTHH:mm:ss'), {showTime:true}) : formatDisplayValue(null);
export const scopeLabel = (value: any) => [
  ...(value?.category_ids ?? []).map((item: any) => item.name),
  ...(value?.location_ids ?? []).map((item: any) => item.name),
  ...(value?.part_ids ?? []).map((item: any) => item.name),
  ...(value?.stock_ids ?? []).map((item: any) => item.name)
].filter(Boolean).join(' · ') || t`All inventory`;

export function useInventoryCapabilities() {
  const api = useApi();
  return useQuery({queryKey:['inventory-capabilities'], queryFn: () => api.get(`${inventoryUrl}capabilities/`).then(r => r.data), staleTime:30000});
}

export function scopeFields(): ApiFormFieldSet {
  return {
    category: {label:t`Category`,field_type:'related field',model:ModelType.partcategory,api_url:apiUrl(ApiEndpoints.category_list),required:false,description:t`Leave blank for all categories, including subcategories.`},
    location: {label:t`Location`,field_type:'related field',model:ModelType.stocklocation,api_url:apiUrl(ApiEndpoints.stock_location_list),required:false,description:t`Leave blank for all locations, including sublocations.`}
  };
}
export function lineFields(unrecorded = false, units?: string): ApiFormFieldSet {
  return {
    ...(unrecorded ? {
      location_native_id:{label:t`Location`,field_type:'related field' as const,model:ModelType.stocklocation,api_url:apiUrl(ApiEndpoints.stock_location_list),required:true},
      batch:{label:t`Batch`,field_type:'string' as const,required:false,description:t`Leave blank only when there is no batch.`},
      identification:{label:t`Physical identification`,field_type:'string' as const,required:true}
    } : {}),
    counted_quantity:{label:`${t`Counted quantity`} (${formatUnit(units)})`,field_type:units === 'count' ? 'integer' : 'decimal',required:false,description:t`Blank means not counted. Enter 0 to confirm zero. Saving does not change stock.`},
    notes:{label:t`Notes`,field_type:'string',required:false}
  };
}

/** Translate business problems without exposing internal identifiers or diagnostic JSON. */
export function problemLabel(code: string) {
  const labels: Record<string,string> = {
    coverage_unavailable:t`Historical coverage changed. Create a new count from the available reference date.`,
    reconciliation_required:t`Attribute the physical quantities after the structural change.`,
    part_conversion_unproven:t`The part changed. Physical identity must be verified before applying this count.`,
    unit_conversion_unproven:t`The unit changed. A verified conversion is required before applying this count.`,
    negative_intermediate:t`A past movement would produce negative stock. Reconcile its history before applying this count.`,
    negative_target:t`The resulting stock would be negative. Review the count and its history.`,
    invalid_attribution:t`Enter a nonnegative quantity for every physical lot and explain the attribution.`,
    unbalanced_attribution:t`The attribution must balance the physical quantity and each location.`,
    discovery_identification_required:t`Identify the discovered stock, its location and batch.`,
    native_stock_unavailable:t`A counted lot is unavailable. Review the structural attribution.`,
    correction_quantity_required:t`Enter an explicit corrected quantity for each previously counted line.`,
    correction_has_newer_version:t`Correct the most recent version of this count.`,
    correction_reference_mismatch:t`A correction must keep the original date and scope.`,
    replacement_requires_deleted_stock:t`A replacement lot is allowed only for a deleted physical identity.`,
    current_location_changed:t`This stock moved after the observation. Keep its current location or review it in a new count.`,
    native_location_unavailable:t`The proposed location is unavailable. Choose an existing storage location.`,
    invalid_resolution:t`Review the structural attribution and try again.`
  };
  return labels[code] ?? t`This count cannot be applied. Review the quantities and their history.`;
}
